import React, { Suspense, useCallback, useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { AnimatedDialog, AnimatedDialogContent, AnimatedDialogHeader, AnimatedDialogTitle } from "src/components/ui/animated-dialog";
import { Button } from "src/components/ui/button";
import { LoadingSpinner } from "src/components/ui/loading-spinner";
import { WizardLayout, WizardStepIndicator } from "src/components/wizard";
import { useToast } from "src/hooks/use-toast";
import { useProductBranding } from "src/hooks/useProductBranding";

import type { NewCapabilityWizardCreateMutation } from "./__generated__/NewCapabilityWizardCreateMutation.graphql";
import type { NewCapabilityWizardQuery } from "./__generated__/NewCapabilityWizardQuery.graphql";
import { StepIllustration } from "./components/StepIllustration";
import { Step1SelectWorkflow } from "./steps/Step1SelectWorkflow";
import { Step2Parameters } from "./steps/Step2Parameters";
import { Step3Configure } from "./steps/Step3Configure";
import { Step4Review } from "./steps/Step4Review";
import {
  createInitialWizardState,
  getWizardStepDescriptions,
  type Parameter,
  WIZARD_STEP_TITLES,
  type WizardState,
  type WizardStep,
} from "./types";

const newCapabilityWizardQuery = graphql`
  query NewCapabilityWizardQuery {
    me {
      ...appMe
      id
      organization {
        id
        integrations {
          edges {
            node {
              id
              name
              integrationType
              githubAccountName
              buildkiteOrgSlug
            }
          }
        }
      }
    }
  }
`;

const createCapabilityMutation = graphql`
  mutation NewCapabilityWizardCreateMutation($input: CreateCapabilityInput!) {
    createCapability(input: $input) {
      capability {
        id
        name
      }
      error
    }
  }
`;

interface NewCapabilityWizardProps {
  isOpen: boolean;
  onClose: (created?: boolean) => void;
}

export function NewCapabilityWizard({ isOpen, onClose }: NewCapabilityWizardProps) {
  const [state, setState] = useState<WizardState>(createInitialWizardState);
  const { toast } = useToast();
  const { productName } = useProductBranding();
  const wizardStepDescriptions = getWizardStepDescriptions(productName);

  const data = useLazyLoadQuery<NewCapabilityWizardQuery>(newCapabilityWizardQuery, {});
  const [commitCreate, isCreating] = useMutation<NewCapabilityWizardCreateMutation>(createCapabilityMutation);

  // Reset state when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setState(createInitialWizardState());
    }
  }, [isOpen]);

  const updateState = useCallback((updates: Partial<WizardState>) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleClose = () => {
    setState(createInitialWizardState());
    onClose();
  };

  const handleBack = () => {
    if (state.currentStep > 1) {
      updateState({ currentStep: (state.currentStep - 1) as WizardStep });
    } else {
      handleClose();
    }
  };

  const convertParametersToSchema = (params: Parameter[]): Record<string, unknown> => {
    const schema: Record<string, unknown> = {};
    params.forEach((param) => {
      const paramSchema: Record<string, unknown> = {
        type: param.type === "select" ? "string" : param.type,
        description: param.description,
      };

      if (param.required) {
        paramSchema.required = true;
      }

      if (param.defaultValue !== undefined && param.defaultValue !== "") {
        let defaultValue: unknown = param.defaultValue;
        if (param.type === "boolean") {
          defaultValue = param.defaultValue.toLowerCase() === "true";
        } else if (param.type === "number") {
          defaultValue = parseFloat(param.defaultValue);
        }
        paramSchema.default = defaultValue;
      }

      if (param.type === "select" && param.selectOptions) {
        paramSchema.enum = param.selectOptions;
      }

      if (param.type === "object" && param.properties) {
        paramSchema.properties = convertParametersToSchema(param.properties);
      }

      schema[param.name] = paramSchema;
    });
    return schema;
  };

  // Get the selected Buildkite integration to get the org slug
  const selectedBuildkiteIntegration = React.useMemo(() => {
    const edges = data.me.organization.integrations.edges ?? [];
    const buildkiteIntegrations = edges.filter((edge) => edge.node.integrationType === "BUILDKITE").map((edge) => edge.node);
    if (state.selectedBuildkiteOrgId) {
      return buildkiteIntegrations.find((integration) => integration.id === state.selectedBuildkiteOrgId);
    }
    return buildkiteIntegrations.length === 1 ? buildkiteIntegrations[0] : undefined;
  }, [data.me.organization.integrations.edges, state.selectedBuildkiteOrgId]);

  const handleSubmit = useCallback(() => {
    let targetConfig: Record<string, unknown> = {};
    let capabilityName = state.name;

    if (state.capabilityType === "GITHUB_ACTIONS_WORKFLOW") {
      const repo = state.githubRepos.find((r) => r.id === parseInt(state.selectedGitHubRepo, 10));
      const workflow = state.githubWorkflows.find((w) => w.id === parseInt(state.selectedGitHubWorkflow, 10));

      if (!repo || !workflow) {
        toast({
          title: "Error",
          description: "Please select a repository and workflow",
          variant: "error",
        });
        return;
      }

      targetConfig = {
        repo_full_name: repo.full_name,
        workflow_id: workflow.id,
        workflow_name: workflow.name,
        ref: state.githubWorkflowDefaultBranch,
      };

      if (!capabilityName) {
        capabilityName = workflow.name;
      }
    } else if (state.capabilityType === "BUILDKITE_PIPELINE") {
      const pipeline = state.buildkitePipelines.find((p) => p.slug === state.selectedBuildkitePipeline);

      if (!pipeline) {
        toast({
          title: "Error",
          description: "Please select a pipeline",
          variant: "error",
        });
        return;
      }

      const buildkiteOrgSlug = selectedBuildkiteIntegration?.buildkiteOrgSlug;
      if (!buildkiteOrgSlug) {
        toast({
          title: "Error",
          description: "Buildkite organization slug not found",
          variant: "error",
        });
        return;
      }

      targetConfig = {
        org_slug: buildkiteOrgSlug,
        pipeline_slug: pipeline.slug,
        branch: state.buildkitePipelineDefaultBranch,
        commit: "HEAD",
      };

      if (!capabilityName) {
        capabilityName = pipeline.name;
      }
    }

    if (!state.description) {
      toast({
        title: "Error",
        description: "Please provide a description",
        variant: "error",
      });
      return;
    }

    let parametersSchema: Record<string, unknown>;
    if (state.showAdvanced) {
      try {
        parametersSchema = JSON.parse(state.jsonSchema);
      } catch {
        toast({
          title: "Error",
          description: "Invalid JSON Schema format",
          variant: "error",
        });
        return;
      }
    } else {
      const schema = convertParametersToSchema(state.parameters);
      parametersSchema = {
        type: "object",
        properties: schema,
        required: state.parameters.filter((p) => p.required).map((p) => p.name),
      };
    }

    commitCreate({
      variables: {
        input: {
          name: capabilityName,
          description: state.description,
          capabilityType: state.capabilityType,
          approvalStyle: state.approvalType,
          targetConfig,
          parametersSchema,
          integrationId: state.selectedIntegrationId,
          isActive: true,
        },
      },
      onCompleted: (response) => {
        if (response.createCapability.error) {
          toast({
            title: "Error",
            description: response.createCapability.error,
            variant: "error",
          });
        } else if (response.createCapability.capability?.name) {
          toast({
            title: "Capability created",
            description: `"${response.createCapability.capability.name}" has been created successfully.`,
            variant: "success",
          });
          setState(createInitialWizardState());
          onClose(true);
        }
      },
      onError: (error) => {
        toast({
          title: "Error",
          description: error.message,
          variant: "error",
        });
      },
    });
  }, [state, selectedBuildkiteIntegration, commitCreate, toast, handleClose, convertParametersToSchema]);

  // Get integrations from data
  const githubIntegrations = React.useMemo(() => {
    const edges = data.me.organization.integrations.edges ?? [];
    return edges.filter((edge) => edge.node.integrationType === "GITHUB").map((edge) => edge.node);
  }, [data.me.organization.integrations.edges]);

  const buildkiteIntegrations = React.useMemo(() => {
    const edges = data.me.organization.integrations.edges ?? [];
    return edges.filter((edge) => edge.node.integrationType === "BUILDKITE").map((edge) => edge.node);
  }, [data.me.organization.integrations.edges]);

  // Step validation logic
  const isStepValid = useCallback(
    (step: WizardStep): boolean => {
      switch (step) {
        case 1:
          if (state.capabilityType === "GITHUB_ACTIONS_WORKFLOW") {
            if (githubIntegrations.length > 1 && !state.selectedGitHubOrgId) return false;
            return !!state.selectedGitHubRepo && !!state.selectedGitHubWorkflow;
          }
          if (state.capabilityType === "BUILDKITE_PIPELINE") {
            if (buildkiteIntegrations.length > 1 && !state.selectedBuildkiteOrgId) return false;
            return !!state.selectedBuildkitePipeline;
          }
          return true;
        case 2:
          // Parameters step is always valid (optional)
          return true;
        case 3:
          return !!state.description.trim();
        case 4:
          return !!state.name?.trim();
        default:
          return false;
      }
    },
    [state, githubIntegrations, buildkiteIntegrations]
  );

  const handleNext = useCallback(() => {
    if (state.currentStep < 4) {
      updateState({ currentStep: (state.currentStep + 1) as WizardStep });
    }
  }, [state.currentStep, updateState]);

  const renderStepContent = () => {
    switch (state.currentStep) {
      case 1:
        return (
          <Step1SelectWorkflow
            state={state}
            onUpdate={updateState}
            githubIntegrations={githubIntegrations}
            buildkiteIntegrations={buildkiteIntegrations}
          />
        );
      case 2:
        return <Step2Parameters state={state} onUpdate={updateState} />;
      case 3:
        return <Step3Configure state={state} onUpdate={updateState} />;
      case 4:
        return (
          <Step4Review
            state={state}
            onUpdate={updateState}
            githubIntegrations={githubIntegrations}
            buildkiteIntegrations={buildkiteIntegrations}
          />
        );
      default:
        return null;
    }
  };

  return (
    <AnimatedDialog open={isOpen} onOpenChange={handleClose}>
      <AnimatedDialogContent variant="full" hideCloseButton={false} dismissOnOutsideClick={false}>
        <AnimatedDialogHeader>
          <AnimatedDialogTitle className="text-foreground text-xl">Create Capability</AnimatedDialogTitle>
        </AnimatedDialogHeader>

        <WizardLayout
          formContent={
            <Suspense
              fallback={
                <div className="flex items-center justify-center py-12">
                  <LoadingSpinner className="h-8 w-8 text-muted-foreground" />
                </div>
              }
            >
              <div className="space-y-6">{renderStepContent()}</div>
            </Suspense>
          }
          illustrationContent={<StepIllustration step={state.currentStep} />}
          stepTitle={WIZARD_STEP_TITLES[state.currentStep]}
          stepDescription={wizardStepDescriptions[state.currentStep]}
        />

        {/* Footer with step indicator and navigation */}
        <div className="mt-6 pt-4 border-t border-border">
          <div className="flex justify-between items-center">
            <Button variant="ghost" onClick={handleBack} className="text-muted-foreground hover:text-foreground">
              {state.currentStep === 1 ? "Cancel" : "\u2190 Back"}
            </Button>

            <WizardStepIndicator
              currentStep={state.currentStep}
              totalSteps={4}
              onStepClick={(step) => {
                if (step < state.currentStep) {
                  updateState({ currentStep: step as WizardStep });
                }
              }}
            />

            {state.currentStep < 4 ? (
              <Button onClick={handleNext} disabled={!isStepValid(state.currentStep)} size="lg">
                Continue
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={!isStepValid(state.currentStep) || isCreating} size="lg">
                {isCreating ? "Creating..." : "Create Capability"}
              </Button>
            )}
          </div>
        </div>
      </AnimatedDialogContent>
    </AnimatedDialog>
  );
}
